import { noop } from 'lodash';
import { type ComponentPropsWithRef, type ComponentType, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { type Board, type Clip } from '@air/api/types';
import { type GallerySection, type GalleryViewProps } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { DropRearrangeArea } from '~/components/DropRearrangeArea/DropRearrangeArea';
import {
  PrivateSearchCommandDragOverlay,
  type PrivateSearchCommandDropPayload,
} from '~/components/Gallery/PrivateSearchCommandDragOverlay';
import { type GalleryItemType } from '~/components/Gallery/types';
import {
  AssetGalleryCardSize,
  type UseGalleryAssetsParams,
  useGalleryAssets,
} from '~/components/Gallery/useGalleryAssets';
import {
  BoardGalleryCardDesktopSize,
  BoardGalleryCardMobileSize,
  type UseGalleryBoardParams,
  useGalleryBoards,
} from '~/components/Gallery/useGalleryBoards';
import { FileGalleryCardSize, type UseGalleryFilesParams, useGalleryFiles } from '~/components/Gallery/useGalleryFiles';
import { type UseGallerySectionHeaderParams } from '~/components/Gallery/useGallerySectionHeader';
import { modifySectionHeaderToEmpty } from '~/components/Gallery/utils';
import LoadMoreSpinner from '~/components/LoadMoreSpinner';
import { PrivateBoardNullState } from '~/components/PrivateBoard/PrivateBoardNullState';
import {
  SelectableGalleryView,
  type SelectableGalleryViewProps,
} from '~/components/SelectableGalleryView/SelectableGalleryView';
import { type GalleryItemToRearrange } from '~/components/Shared/ClipList/RearrangeableRow';
import DragType, { boardDragTypes, type RearrangableItemType } from '~/components/Shared/Drag/dragTypes';
import { usePermissionedVisibleCustomFieldsLength } from '~/components/v2/hooks/usePermissionedVisibleCustomFieldsLength';
import { ASSET_CARD_ROW_MT } from '~/constants/assetCard';
import { WKSPC_CLIP_INFO_HEIGHT, WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT } from '~/constants/WorkspaceSpacing';
import { usePrivateSmartSearchSimilarToFilter } from '~/hooks/filters/usePrivateSmartSearchSimilarToFilter';
import { useCardSizePreference } from '~/hooks/useCardSizePreference';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useGroupedGallerySelectedItems } from '~/hooks/useGroupedGallerySelectedItems';
import { useRearrangeableGalleryItems } from '~/hooks/useRearrangeableGalleryItems';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { currentSortFieldNameSelector, isTitleAndMetadataVisibleSelector } from '~/store/configViews/selectors';
import { usePrivateGalleryViewItems } from '~/swr-hooks/gallery/galleryView/usePrivateGalleryViewItems';
import { useAirSelector } from '~/utils/ReduxUtils';
import { getAssetsAndFilesUploads } from '~/utils/Uploader';

const rearrangeTypes = [DragType.asset, DragType.file, ...boardDragTypes];

export type GalleryMetadata = {
  isSelectable: boolean;
  type: GalleryItemType;
  itemId: string;
  canDragTo?: (item: GalleryItemToRearrange) => boolean;
};

export type PrivateGalleryViewProps = Pick<ComponentPropsWithRef<'div'>, 'className'> &
  Pick<UseGallerySectionHeaderParams, 'renderHeader'> & {
    assetItemAdditionalHeight?: number;
    assetItemHorizontalSpacing?: number;
    boardItemHorizontalSpacing?: number;
    boardItemHeight?: number;
    boardItemWidth?: number;
    canRearrange?: boolean;
    containerHorizontalPadding?: number;
    EmptyState?: ComponentType;
    fileItemHeight?: number;
    fileItemAdditionalHeight?: number;
    fileItemHorizontalSpacing?: number;
    headerContainerHeight?: number;
    renderAsset?: UseGalleryAssetsParams<Clip>['renderAsset'];
    renderFile?: UseGalleryFilesParams<Clip>['renderFile'];
    renderBoard?: UseGalleryBoardParams<Board>['renderBoard'];
    rowVerticalSpacing?: number;
    scrollElementRef: SelectableGalleryViewProps['scrollElementRef'];
    showBoards: boolean;
    showUploads?: boolean;
    includeCoverImage?: boolean;
    includeDirectDescendantCount?: boolean;
    includeDirectDescendantBoardCount?: boolean;
    showAssetHeader?: boolean;
  };

export const PrivateGalleryView = memo(
  ({
    assetItemAdditionalHeight,
    assetItemHorizontalSpacing,
    boardItemHorizontalSpacing,
    boardItemHeight,
    boardItemWidth,
    canRearrange = false,
    className,
    containerHorizontalPadding,
    EmptyState = PrivateBoardNullState,
    fileItemHeight,
    fileItemAdditionalHeight,
    fileItemHorizontalSpacing,
    headerContainerHeight,
    renderFile,
    renderAsset,
    renderBoard,
    renderHeader,
    rowVerticalSpacing,
    scrollElementRef,
    showBoards,
    showUploads = true,
    includeCoverImage,
    includeDirectDescendantCount,
    includeDirectDescendantBoardCount,
    showAssetHeader = true,
  }: PrivateGalleryViewProps) => {
    const { crossLocationSelectionEnabled } = useFeatureFlags();
    const [boardsExpanded, setBoardsExpanded] = useState(true);
    const [assetsExpanded, setAssetsExpanded] = useState(true);

    const { isAboveSmallScreen, isAboveMediumScreen } = useBreakpointsContext();
    const { cardSize } = useCardSizePreference();
    const { isSemanticSearchEnabled, isHybridSemanticSearchEnabled } = usePrivateSearchContext();
    const { addSimilarToFilter } = usePrivateSmartSearchSimilarToFilter();
    const {
      data: { boards: boardsData, clips: clipsData, files: filesData, uploads },
      isLoading,
      loadNextPage,
      isEmpty,
    } = usePrivateGalleryViewItems({
      showBoards,
      boardsExpanded,
      assetsExpanded,
      showUploads,
      semantic: isSemanticSearchEnabled,
      hybrid: isHybridSemanticSearchEnabled,
      includeCoverImage,
      includeDirectDescendantCount,
      includeDirectDescendantBoardCount,
    });

    useFetchObjectsPermissions({
      objects: {
        boardIds: boardsData?.items.map(({ id }) => id),
        clipIds: [...clipsData.items, ...filesData.items]?.map(({ id }) => id),
        /**
         * We need to fetch permissions for assets because we use their permissions for certain endpoints
         * @see https://air-labs-team.slack.com/archives/C052RDYCGAG/p1695838522133639
         */
        assetIds: [...clipsData.items, ...filesData.items]?.map(({ assetId }) => assetId),
      },
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const currentSortFieldName = useAirSelector(currentSortFieldNameSelector);
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
    const { permissionedVisibleCustomFieldsLength } = usePermissionedVisibleCustomFieldsLength();

    const { assetUploads, fileUploads } = useMemo(() => getAssetsAndFilesUploads(uploads || []), [uploads]);

    const { rearrangeItems } = useRearrangeableGalleryItems({
      assets: clipsData?.items ?? [],
      boards: boardsData?.items ?? [],
      files: filesData?.items ?? [],
    });

    useGroupedGallerySelectedItems(
      boardsData?.items || [],
      clipsData?.items ?? [],
      filesData?.items ?? [],
      crossLocationSelectionEnabled,
    );

    // add extra space to row for wider drop-rearrange area
    const { width = 0, ref: widthRef } = useResizeDetector<HTMLDivElement>({
      handleHeight: false,
      refreshMode: 'throttle',
      refreshRate: 300,
    });

    const boardsSection = useGalleryBoards({
      data: boardsData,
      containerWidth: width,
      renderBoard,
      loadMore: loadNextPage,
      containerHorizontalPadding: containerHorizontalPadding ?? responsiveHorizontalPadding,
      itemHorizontalSpacing: boardItemHorizontalSpacing,
      isSelectable: () => true,
      onSectionCollapse: (isCollapsed) => setBoardsExpanded(!isCollapsed),
      itemHeight:
        boardItemHeight ??
        (isAboveSmallScreen ? BoardGalleryCardDesktopSize[cardSize] : BoardGalleryCardMobileSize[cardSize]),
      itemWidth: boardItemWidth,
      headerContainerHeight,
      renderHeader,
      rowVerticalSpacing,
    });

    const fileAdditionalHeight = useMemo(() => {
      const additionalRows = Math.max(0, permissionedVisibleCustomFieldsLength - 2);

      return additionalRows * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
    }, [permissionedVisibleCustomFieldsLength]);

    const assetAdditionalHeight = useMemo(() => {
      let height = 0;

      if (isTitleAndMetadataVisible) height += WKSPC_CLIP_INFO_HEIGHT;
      if (permissionedVisibleCustomFieldsLength) height += ASSET_CARD_ROW_MT;

      return height + permissionedVisibleCustomFieldsLength * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
    }, [permissionedVisibleCustomFieldsLength, isTitleAndMetadataVisible]);

    const assetsSection = useGalleryAssets({
      renderAsset,
      isFirstSection: !boardsData?.items?.length,
      data: clipsData,
      containerWidth: width,
      uploads: assetUploads,
      loadMore: loadNextPage,
      containerHorizontalPadding: containerHorizontalPadding ?? responsiveHorizontalPadding,
      itemAdditionalHeight: assetItemAdditionalHeight ?? assetAdditionalHeight,
      itemHorizontalSpacing: assetItemHorizontalSpacing,
      isSelectable: () => true,
      onSectionCollapse: (isCollapsed) => setAssetsExpanded(!isCollapsed),
      itemHeight: AssetGalleryCardSize[cardSize],
      headerContainerHeight,
      renderHeader,
      rowVerticalSpacing,
    });

    const filesSection = useGalleryFiles({
      renderFile,
      isFirstSection: !boardsData?.items?.length && !clipsData?.items.length,
      data: filesData,
      containerWidth: width,
      uploads: fileUploads,
      loadMore: loadNextPage,
      containerHorizontalPadding: containerHorizontalPadding ?? responsiveHorizontalPadding,
      isSelectable: () => true,
      itemHeight: fileItemHeight ?? FileGalleryCardSize[cardSize],
      itemAdditionalHeight: fileItemAdditionalHeight ?? fileAdditionalHeight,
      itemWidth: !isAboveMediumScreen ? 300 : undefined,
      itemHorizontalSpacing: fileItemHorizontalSpacing ?? (!isAboveSmallScreen ? 0 : 24),
      headerContainerHeight,
      renderHeader,
      rowVerticalSpacing,
    });

    const sections = [
      boardsSection,
      modifySectionHeaderToEmpty({
        section: assetsSection,
        showHeader: showAssetHeader,
        isFirstSection: !!boardsData?.items?.length,
      }),
      filesSection,
    ].filter((section): section is GallerySection<GalleryMetadata> => !!section);

    const rowRenderer: Required<GalleryViewProps>['rowRenderer'] = useCallback(
      ({ itemRenderer, style, row, isVisible, index }) => (
        /**
         * index is the index of the row
         * itemIndex is the index of the item in the row
         * item.index is the index of the item in the entire list
         */
        <div style={style} key={index}>
          {row.map((item, itemIndex) => {
            //@ts-expect-error
            const type: GalleryItemType = item.getData?.(itemIndex)?.type;

            return (
              <div key={item.index}>
                {itemIndex === 0 && (
                  <DropRearrangeArea
                    dropLocation="asset"
                    onItemRearrange={(draggedItem: RearrangableItemType) => {
                      rearrangeItems(draggedItem, {
                        index: item.index,
                        direction: 'before',
                        type,
                      });
                    }}
                    containerProps={{
                      style: {
                        left: item.box.left - 12,
                        top: 0,
                        height: item.box.height,
                        width: 24,
                        justifyContent: 'center',
                      },
                    }}
                    barProps={{
                      className: 'w-1 h-full',
                    }}
                    dragTypes={rearrangeTypes}
                    canDropItem={(draggedItem) => draggedItem.index !== item.index && canRearrange}
                  />
                )}
                {itemRenderer(item, isVisible)}
                <DropRearrangeArea
                  dropLocation="asset"
                  onItemRearrange={(draggedItem: RearrangableItemType) => {
                    rearrangeItems(draggedItem, {
                      index: item.index,
                      direction: 'after',
                      type,
                    });
                  }}
                  containerProps={{
                    style: {
                      left: item.box.width + item.box.left,
                      top: 0,
                      height: item.box.height,
                      width: 24,
                    },
                  }}
                  barProps={{
                    className: 'w-1 h-full',
                  }}
                  dragTypes={rearrangeTypes}
                  canDropItem={(draggedItem) => draggedItem.index !== item.index && canRearrange}
                />
              </div>
            );
          })}
        </div>
      ),
      [canRearrange, rearrangeItems],
    );

    const onAssetDroppedOnSearchCommand = useCallback(
      ({ item }: PrivateSearchCommandDropPayload) => {
        addSimilarToFilter(item);
      },
      [addSimilarToFilter],
    );

    return (
      <>
        <div ref={widthRef} />
        <PrivateSearchCommandDragOverlay onDrop={onAssetDroppedOnSearchCommand} />
        {isLoading ? (
          <LoadMoreSpinner isVisible isLoading={isLoading} loadMore={noop} />
        ) : isEmpty ? (
          <EmptyState />
        ) : width ? (
          <div ref={containerRef} className={cn('relative mt-6 min-h-full', className)}>
            <SelectableGalleryView
              scrollElementRef={scrollElementRef}
              containerRef={containerRef}
              sections={sections}
              width={width}
              rowRenderer={currentSortFieldName === 'customSort' ? rowRenderer : undefined}
            />
          </div>
        ) : null}
      </>
    );
  },
);

PrivateGalleryView.displayName = 'PrivateGalleryView';
